import { useCallback, useMemo } from 'react';
import { TemplateGalleryCard, TemplateQueryResponse } from '@common/types';
import { useErrorMonitor, useHttpClient } from '@wix/fe-essentials-standalone';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  addFavoriteTemplate,
  queryFavoriteTemplateIds,
  removeFavoriteTemplate,
} from './templateFavoritesApi';
import { templateFavoritesQueryKeys } from './templateFavoritesQueryKeys';

type ToggleFavoriteTemplateParams = {
  listingId: string;
  shouldFavorite: boolean;
};

export const useTemplateFavorites = ({
  enabled = true,
  onToggleError,
}: {
  enabled?: boolean;
  onToggleError?: (shouldFavorite: boolean) => void;
} = {}) => {
  const httpClient = useHttpClient();
  const errorMonitor = useErrorMonitor();
  const queryClient = useQueryClient();

  const favoriteIdsQuery = useQuery({
    queryKey: templateFavoritesQueryKeys.ids(),
    enabled,
    retry: false,
    queryFn: async () => {
      try {
        return await queryFavoriteTemplateIds(httpClient);
      } catch (error) {
        errorMonitor.captureException(error as Error);
        return [] as string[];
      }
    },
  });

  const toggleFavoriteTemplate = useMutation({
    mutationFn: async ({
      listingId,
      shouldFavorite,
    }: ToggleFavoriteTemplateParams) => {
      if (shouldFavorite) {
        await addFavoriteTemplate(httpClient, listingId);
        return;
      }

      await removeFavoriteTemplate(httpClient, listingId);
    },
    onMutate: async ({ listingId, shouldFavorite }) => {
      await queryClient.cancelQueries({
        queryKey: templateFavoritesQueryKeys.ids(),
      });
      await queryClient.cancelQueries({
        queryKey: templateFavoritesQueryKeys.count(),
      });
      await queryClient.cancelQueries({
        queryKey: templateFavoritesQueryKeys.infiniteTemplates(),
      });

      const previousFavoriteIds = queryClient.getQueryData<string[]>(
        templateFavoritesQueryKeys.ids(),
      );

      const previousCount = queryClient.getQueryData<number>(
        templateFavoritesQueryKeys.count(),
      );
      queryClient.setQueryData<number>(
        templateFavoritesQueryKeys.count(),
        (current) =>
          current !== undefined
            ? Math.max(0, current + (shouldFavorite ? 1 : -1))
            : undefined,
      );

      queryClient.setQueryData<string[]>(
        templateFavoritesQueryKeys.ids(),
        (current = []) =>
          shouldFavorite
            ? [...current, listingId]
            : current.filter((id) => id !== listingId),
      );

      const previousInfiniteTemplates = queryClient.getQueryData<
        InfiniteData<TemplateQueryResponse>
      >(templateFavoritesQueryKeys.infiniteTemplates());

      if (!shouldFavorite) {
        queryClient.setQueryData<InfiniteData<TemplateQueryResponse>>(
          templateFavoritesQueryKeys.infiniteTemplates(),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                templates:
                  page.templates?.filter((t) => t.id !== listingId) ?? [],
              })),
            };
          },
        );
      }

      return { previousFavoriteIds, previousInfiniteTemplates, previousCount };
    },
    onError: (error, variables, context) => {
      errorMonitor.captureException(error as Error);
      queryClient.setQueryData(
        templateFavoritesQueryKeys.ids(),
        context?.previousFavoriteIds ?? [],
      );
      queryClient.setQueryData(
        templateFavoritesQueryKeys.infiniteTemplates(),
        context?.previousInfiniteTemplates,
      );
      queryClient.setQueryData(
        templateFavoritesQueryKeys.count(),
        context?.previousCount,
      );
      onToggleError?.(variables.shouldFavorite);
    },
    onSuccess: (_data, { shouldFavorite }) => {
      queryClient.invalidateQueries({
        queryKey: templateFavoritesQueryKeys.ids(),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: templateFavoritesQueryKeys.count(),
      });
      queryClient.invalidateQueries({
        queryKey: templateFavoritesQueryKeys.templatesRoot(),
        ...(!shouldFavorite && { refetchType: 'none' }),
      });
    },
  });

  const favoriteTemplateIds = useMemo(
    () => new Set(favoriteIdsQuery.data ?? []),
    [favoriteIdsQuery.data],
  );

  const isTemplateFavorite = useCallback(
    (template?: Pick<TemplateGalleryCard, 'id'>) =>
      Boolean(template?.id && favoriteTemplateIds.has(template.id)),
    [favoriteTemplateIds],
  );

  return {
    favoriteTemplateIds,
    isTemplateFavorite,
    isLoadingFavorites: favoriteIdsQuery.isLoading,
    toggleFavoriteTemplate: toggleFavoriteTemplate.mutate,
    pendingFavoriteListingId: toggleFavoriteTemplate.variables?.listingId,
    isTogglePending: toggleFavoriteTemplate.isPending,
  };
};
